import { RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { isConflictError } from '../../api/client';
import type { ApiError } from '../../lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';

export interface ConflictDialogProps {
  /** The 409 / 412 error, or `null` when closed. */
  error: ApiError | null;
  /** Called with "reload": refetch the record (new `configVersion` / `version`) and re-apply the pending change. */
  onReload: () => void | Promise<void>;
  /** Called when the user gives up; the pending change is discarded by the caller. */
  onDismiss: () => void;
  /** What was being edited, for the copy. */
  subject?: string;
  busy?: boolean;
}

/**
 * Optimistic concurrency UX (ADR-0016 §4): a 409 `version_mismatch`/`conflict` or 412
 * `precondition_failed` opens this dialog; "Reload and retry" re-reads the record so the next
 * save carries the current `If-Match`.
 */
export function ConflictDialog({
  error,
  onReload,
  onDismiss,
  subject = 'this record',
  busy = false,
}: ConflictDialogProps) {
  const open = error !== null;
  return (
    <AlertDialog open={open} onOpenChange={(next) => (next ? undefined : onDismiss())}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Someone else changed {subject}</AlertDialogTitle>
          <AlertDialogDescription>
            {error?.problem?.detail ??
              `${capitalize(subject)} was modified since you loaded it, so your change was not saved. Reload the latest version and apply your change again.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onDismiss} disabled={busy}>
            Discard my change
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={(event) => {
              event.preventDefault();
              void onReload();
            }}
            disabled={busy}
          >
            <RefreshCw className="size-4" />
            Reload and retry
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export interface ConflictHandler {
  /** Pass to `<ConflictDialog error=…>`. */
  conflict: ApiError | null;
  /** Wrap a mutation call: conflicts are captured, everything else is re-thrown. */
  guard: <T>(run: () => Promise<T>) => Promise<T | undefined>;
  /** Refetches via `reload`, then re-runs the last guarded call. */
  reloadAndRetry: () => Promise<void>;
  dismiss: () => void;
  busy: boolean;
}

/**
 * Pairs a mutation with the dialog:
 *
 *   const conflict = useConflictHandler(() => project.refetch());
 *   await conflict.guard(() => update.mutateAsync({ id, patch, ifMatch: project.data.configVersion }));
 *   <ConflictDialog {...conflict.dialogProps} />
 *
 * On retry the guarded function runs again, so build `ifMatch` from the *latest* query data
 * inside the closure (read `queryClient.getQueryData` or a ref), not from a stale variable.
 */
export function useConflictHandler(reload: () => Promise<unknown> | unknown) {
  const [conflict, setConflict] = useState<ApiError | null>(null);
  const [last, setLast] = useState<(() => Promise<unknown>) | null>(null);
  const [busy, setBusy] = useState(false);

  const guard = useCallback(async <T,>(run: () => Promise<T>): Promise<T | undefined> => {
    try {
      const result = await run();
      setConflict(null);
      return result;
    } catch (error) {
      if (isConflictError(error)) {
        setConflict(error);
        setLast(() => run);
        return undefined;
      }
      throw error;
    }
  }, []);

  const reloadAndRetry = useCallback(async () => {
    setBusy(true);
    try {
      await reload();
      setConflict(null);
      if (last) await guard(last);
    } finally {
      setBusy(false);
    }
  }, [guard, last, reload]);

  const dismiss = useCallback(() => {
    setConflict(null);
    setLast(null);
  }, []);

  return {
    conflict,
    guard,
    reloadAndRetry,
    dismiss,
    busy,
    dialogProps: { error: conflict, onReload: reloadAndRetry, onDismiss: dismiss, busy },
  };
}
